/**
 * InboxListHeader (desktop)
 *
 * Header that sits at the top of the inquiry-list column. It folds the two
 * controls that used to live in the separate left sidebar into the list itself:
 *
 *   1. Status tabs (All / Open / Closed / Drafts) as a segmented tab bar. This
 *      mirrors the mobile tab model so desktop and mobile share one mental
 *      model.
 *   2. Cancer-type filter as a dropdown, so a long list of types no longer
 *      needs a permanent column. The active selection shows in the trigger,
 *      with a Clear affordance when a type is selected.
 *
 * Removing the left rail collapses the layout to three panes:
 * inquiry list | chat | right sidebar.
 *
 * // DEV ENG-610
 */
import { ChevronDown, XIcon } from 'lucide-react-native';
import { memo } from 'react';
import { twJoin } from 'tailwind-merge';

import type { TabRoute } from '@/components/inbox/types';
import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type CancerType = { shortLabel?: string | null; label?: string | null };

type InboxListHeaderProps = {
  routes: TabRoute[];
  tabSelected: string;
  onTabPress: (index: number) => void;
  inquiryCancerTypesData: CancerType[] | undefined;
  selectedCancerFocus: string | undefined;
  setSelectedCancerFocus: (value: string | undefined) => void;
  resetSelectedChatId: () => void;
};

export const InboxListHeader = memo<InboxListHeaderProps>(function InboxListHeader({
  routes,
  tabSelected,
  onTabPress,
  inquiryCancerTypesData,
  selectedCancerFocus,
  setSelectedCancerFocus,
  resetSelectedChatId,
}) {
  const { colors } = useColors();

  const filterDisabled = tabSelected === 'drafts';

  const hasCancerTypes = (inquiryCancerTypesData?.length ?? 0) > 0;

  const selectedLabel = selectedCancerFocus
    ? (inquiryCancerTypesData?.find((type) => type.shortLabel === selectedCancerFocus)?.label ??
      'Filter by cancer type')
    : 'Filter by cancer type';

  return (
    <VStack className="border-b border-outline-100 bg-background-0">
      {/* Visually-hidden page heading. The three-pane layout dropped the visible
          "Your inquiries" title, so this preserves the screen-reader landmark
          without changing the design. */}
      <Text
        className="sr-only absolute opacity-0"
        role="heading"
        aria-level={1}
        aria-label="Your inquiries">
        Your inquiries
      </Text>

      {/* Status tabs. The tabs row carries its own h-16 + border-b so its bottom
          divider lands at the same pixel as the chat and sidebar headers. */}
      <HStack className="h-20 border-b border-outline-100 px-1">
        {routes.map((route) => {
          const isActive = route.key === tabSelected;

          return (
            <Pressable
              key={route.key}
              testID={`inbox-tab-${route.key}`}
              onPress={() => onTabPress(route.index)}
              className="flex-1 data-[focus-visible=true]:web:ring-inset"
              accessibilityState={{ selected: isActive }}
              {...getAccessibilityProps({ role: 'button', label: route.desktopTitle })}>
              <HStack
                className={twJoin(
                  'h-full items-center justify-center gap-1.5 border-b-2 border-b-transparent px-2',
                  isActive && 'border-b-primary-500'
                )}>
                <Icon
                  as={route.desktopIcon}
                  size="sm"
                  color={isActive ? colors.primary[600] : colors.typography[700]}
                />
                <Text
                  size="sm"
                  className={twJoin(
                    isActive ? 'font-semibold text-primary-600' : 'text-typography-700'
                  )}>
                  {route.desktopTitle}
                </Text>
                {route.count > 0 ? (
                  <Badge
                    testID={`inbox-tab-count-${route.key}`}
                    size="sm"
                    action="muted"
                    className="rounded-full">
                    <BadgeText className="text-typography-600">{route.count}</BadgeText>
                  </Badge>
                ) : null}
              </HStack>
            </Pressable>
          );
        })}
      </HStack>

      {/* List toolbar: cancer-type filter. Clear sits inline inside the filter
          control as an X icon (matching the Directory search input) rather than
          a separate button. */}
      {hasCancerTypes ? (
        <HStack className="min-h-[56px] items-center gap-2 px-3">
          <HStack className="h-9 flex-1 items-center rounded-md bg-background-50 pl-3 pr-2 web:transition-colors web:duration-150 web:focus-within:ring-2 web:focus-within:ring-indicator-info web:hover:bg-background-100">
            <Select
              className="flex-1"
              isDisabled={filterDisabled}
              selectedValue={String(selectedCancerFocus || 'Select All')}
              onValueChange={(label: string) => {
                setSelectedCancerFocus(label === 'Select All' ? undefined : label);

                resetSelectedChatId();
              }}>
              <SelectTrigger className="flex-1 items-center justify-between border-0 bg-transparent px-0 data-[focus=true]:web:shadow-none">
                <SelectInput
                  // Suppress the inner focus ring; the outer control already
                  // provides the boundary, so a second border on focus reads
                  // as a stray box.
                  style={{
                    color: colors.typography[700],
                    outlineWidth: 0,
                    outlineColor: 'transparent',
                  }}
                  placeholder="Filter by cancer type"
                  value={filterDisabled ? 'Filter by cancer type' : selectedLabel}
                />
                {!selectedCancerFocus || filterDisabled ? <SelectIcon as={ChevronDown} /> : null}
              </SelectTrigger>

              <SelectPortal>
                <SelectBackdrop />
                <SelectContent className="max-h-[70vh]">
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>
                  <SelectScrollView>
                    <SelectItem key="all" label="Select All" value="Select All" />
                    {inquiryCancerTypesData?.map((type) => (
                      <SelectItem
                        key={type.shortLabel}
                        label={type.label || ''}
                        value={String(type.shortLabel)}
                      />
                    ))}
                  </SelectScrollView>
                </SelectContent>
              </SelectPortal>
            </Select>

            {selectedCancerFocus && !filterDisabled ? (
              <Pressable
                className="min-h-[44px] min-w-[44px] items-center justify-center pl-1"
                onPress={() => {
                  setSelectedCancerFocus(undefined);

                  resetSelectedChatId();
                }}
                {...getAccessibilityProps({ role: 'button', label: 'Clear cancer type filter' })}>
                <Icon as={XIcon} size="sm" className="text-typography-500" />
              </Pressable>
            ) : null}
          </HStack>
        </HStack>
      ) : null}
    </VStack>
  );
});
